import { useEffect, useState } from 'react'
import { usePairContract, useTokenContract } from './useContract'
import { TokenAmount, Token } from '../module/uniswapSdk'
import { useActiveWeb3React } from './index'

// 合约地址常量
const PAIR_ADDRESS = '0x7d7C5702D53D086a7348212C3eC05A5fbb155B8E'
const TOP_ADDRESS = '0x152e00d27b612Bc6901a411bbad67b26c731FB81'
const TOPUSDT_ADDRESS = '0x0d43D507efDF09e14aE665B36352c4056188D4d8'

interface PoolReserves {
  topReserve: TokenAmount | null
  topUsdtReserve: TokenAmount | null
  loading: boolean
}

/**
 * Hook to fetch pool reserves for Top/TopUSDT pair
 */
export function usePoolReserves(): PoolReserves {
  const { chainId } = useActiveWeb3React()
  const pairContract = usePairContract(PAIR_ADDRESS, false)
  const topTokenContract = useTokenContract(TOP_ADDRESS, false)
  const topUsdtTokenContract = useTokenContract(TOPUSDT_ADDRESS, false)
  const [reserves, setReserves] = useState<PoolReserves>({
    topReserve: null,
    topUsdtReserve: null,
    loading: true
  })

  useEffect(() => {
    if (!pairContract || !chainId || !topTokenContract || !topUsdtTokenContract) {
      setReserves({
        topReserve: null,
        topUsdtReserve: null,
        loading: false
      })
      return
    }

    let cancelled = false

    async function fetchReserves() {
      // TypeScript guard: 确保所有合约和 chainId 都存在
      if (!pairContract || !chainId || !topTokenContract || !topUsdtTokenContract) {
        return
      }

      try {
        // 获取储备量
        const reservesResult = await pairContract.getReserves()
        const reserve0 = reservesResult.reserve0
        const reserve1 = reservesResult.reserve1

        // 获取 token0 和 token1 地址
        const token0Address = await pairContract.token0()
        const token1Address = await pairContract.token1()

        // 确定哪个是 Top，哪个是 TopUSDT
        const isTopToken0 = token0Address.toLowerCase() === TOP_ADDRESS.toLowerCase()
        const isTopUsdtToken0 = token0Address.toLowerCase() === TOPUSDT_ADDRESS.toLowerCase()

        // 获取 decimals
        let topDecimals = 18
        let topUsdtDecimals = 18

        try {
          topDecimals = await topTokenContract.decimals()
          topUsdtDecimals = await topUsdtTokenContract.decimals()
        } catch (error) {
          console.warn('Failed to fetch token decimals, using defaults', error)
        }

        const topToken = new Token(chainId, TOP_ADDRESS, topDecimals, 'Top', 'Top')
        const topUsdtToken = new Token(chainId, TOPUSDT_ADDRESS, topUsdtDecimals, 'TopUSDT', 'TopUSDT')

        // 根据 token0/token1 分配储备量
        let topReserve: TokenAmount
        let topUsdtReserve: TokenAmount

        if (isTopToken0) {
          topReserve = new TokenAmount(topToken, reserve0.toString())
          topUsdtReserve = new TokenAmount(topUsdtToken, reserve1.toString())
        } else if (isTopUsdtToken0) {
          topUsdtReserve = new TokenAmount(topUsdtToken, reserve0.toString())
          topReserve = new TokenAmount(topToken, reserve1.toString())
        } else {
          // 如果都不匹配，根据地址比较来确定
          // 这种情况不应该发生，但作为后备方案
          const topIsToken0 =
            token0Address.toLowerCase() < token1Address.toLowerCase()
              ? TOP_ADDRESS.toLowerCase() < TOPUSDT_ADDRESS.toLowerCase()
              : TOP_ADDRESS.toLowerCase() > TOPUSDT_ADDRESS.toLowerCase()

          if (topIsToken0) {
            topReserve = new TokenAmount(topToken, reserve0.toString())
            topUsdtReserve = new TokenAmount(topUsdtToken, reserve1.toString())
          } else {
            topReserve = new TokenAmount(topToken, reserve1.toString())
            topUsdtReserve = new TokenAmount(topUsdtToken, reserve0.toString())
          }
        }

        if (!cancelled) {
          setReserves({
            topReserve,
            topUsdtReserve,
            loading: false
          })
        }
      } catch (error) {
        console.error('Error fetching pool reserves:', error)
        if (!cancelled) {
          setReserves({
            topReserve: null,
            topUsdtReserve: null,
            loading: false
          })
        }
      }
    }

    fetchReserves()

    // 设置轮询，每 10 秒更新一次
    const interval = setInterval(fetchReserves, 10000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [pairContract, chainId, topTokenContract, topUsdtTokenContract])

  return reserves
}
